<script setup>
import InputText from 'primevue/inputtext'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import Checkbox from 'primevue/checkbox'
import RadioButton from 'primevue/radiobutton'
import { ref } from 'vue'

const value1 = ref(null)
const value2 = ref(null)
const value3 = ref(null)
const value = ref(null)
const unchecked = ref(false)
const checked = ref(true)
const rb1 = ref('')
const rb2 = ref('a')
const rb3 = ref('')
const rb4 = ref('b')
const rb5 = ref('')
const rb6 = ref('c')
const rb7 = ref('')
const rb8 = ref('d')
const pizza = ref()
</script>

<template>
  <h3 class="header-md pb-1">Text Inputs</h3>
  <div class="card flex flex-col items-start justify-items-start gap-4 pb-4">
    <div class="flex flex-col gap-2">
      <label for="username">
        Username
        <span class="absolute ml-2"
          ><span class="material-icons-round text-primary-400">help</span></span
        >
      </label>
      <InputText id="username" v-model="value" aria-describedby="username-help" />
      <small id="username-help">Enter your username to reset your password.</small>
    </div>
    <div class="mb-3 flex flex-wrap items-center gap-2">
      <label for="username">Username</label>
      <InputText id="username" v-model="value" invalid aria-label="invalid input" />
      <GbInlineMessage gbTheme="red"
        >Username is required<template #icon
          ><span class="material-icons-outlined">public</span></template
        ></GbInlineMessage
      >
    </div>
    <InputText v-model="value" disabled placeholder="Disabled" aria-label="disabled input" />
    <InputText
      v-model="value1"
      type="text"
      size="small"
      placeholder="Small"
      aria-label="small input"
    />
    <InputText v-model="value2" type="text" placeholder="Normal" aria-label="normal input" />
    <InputText
      v-model="value3"
      type="text"
      size="large"
      placeholder="Large"
      aria-label="large input"
    />
    <span class="relative">
      <span class="absolute left-3 mt-2">
        <span class="material-icons-outlined">public</span>
      </span>
      <InputText
        v-model="value1"
        placeholder="Search"
        class="w-full"
        :pt="{ root: { class: ['!pl-10'] } }"
        :ptOptions="{ mergeSections: true, mergeProps: true }"
        aria-label="input with icon left"
      />
    </span>

    <span class="relative">
      <span class="absolute right-3 mt-2"><span class="material-icons-outlined">public</span></span>
      <InputText v-model="value2" class="pr-10" aria-label="input with icon right" />
    </span>
  </div>
  <h3 class="header-md pb-1">Checkbox</h3>
  <div class="flex flex-col items-start justify-items-start gap-4 pb-4">
    <div class="flex gap-2">
      <label for="checkbox1">Checkbox</label>
      <Checkbox v-model="unchecked" :binary="true" inputId="checkbox1" />
    </div>
    <div class="flex gap-2">
      <label for="checkbox2"> Checkbox Checked</label>
      <Checkbox v-model="checked" :binary="true" inputId="checkbox2" />
    </div>
    <div class="flex gap-2">
      <label for="checkbox3">Disabled checkbox</label>
      <Checkbox v-model="unchecked" :binary="true" disabled inputId="checkbox3" />
    </div>
    <div class="flex gap-2">
      <label for="checkbox4">Disabled Checkbox Checked</label>
      <Checkbox v-model="checked" :binary="true" disabled inputId="checkbox4" />
    </div>
    <div class="flex items-center gap-2">
      <label for="checkbox5">Small Checkbox</label>
      <Checkbox
        v-model="unchecked"
        :binary="true"
        inputId="checkbox5"
        size="small"
        gbTheme="purple"
      />
    </div>
    <div class="flex items-center gap-2">
      <label for="checkbox6">Small Checkbox Checked</label>
      <Checkbox
        v-model="checked"
        :binary="true"
        inputId="checkbox6"
        size="small"
        gbTheme="purple"
      />
    </div>
    <div class="flex items-center gap-2">
      <label for="checkbox7">Small Disabled Checkbox</label>
      <Checkbox
        v-model="unchecked"
        :binary="true"
        disabled
        inputId="checkbox7"
        size="small"
        gbTheme="purple"
      />
    </div>
    <div class="flex items-center gap-2">
      <label for="checkbox8">Small Disabled Checkbox checked</label>
      <Checkbox
        v-model="checked"
        :binary="true"
        disabled
        inputId="checkbox8"
        size="small"
        gbTheme="purple"
      />
    </div>
  </div>
  <h4 class="header-sm pb-1">Checkbox Group</h4>
  <div class="card flex flex-wrap gap-3 pb-6 pt-1">
    <span class="font-bold">Pizza Toppings</span>
    <div class="flex items-center">
      <Checkbox v-model="pizza" inputId="ingredient1" name="pizza" value="Cheese" />
      <label for="ingredient1" class="ml-2"> Cheese </label>
    </div>
    <div class="flex items-center">
      <Checkbox v-model="pizza" inputId="ingredient2" name="pizza" value="Mushroom" />
      <label for="ingredient2" class="ml-2"> Mushroom </label>
    </div>
    <div class="flex items-center">
      <Checkbox v-model="pizza" inputId="ingredient3" name="pizza" value="Pepper" />
      <label for="ingredient3" class="ml-2"> Pepper </label>
    </div>
    <div class="flex items-center">
      <Checkbox v-model="pizza" inputId="ingredient4" name="pizza" value="Onion" />
      <label for="ingredient4" class="ml-2"> Onion </label>
    </div>
  </div>
  <h4 class="header-sm pb-1">Label Position Variations</h4>
  <div class="w-36">
    <div class="flex flex-col gap-2 py-2">
      <label for="checkbox1" class="flex justify-center">Top Label</label>
      <Checkbox v-model="unchecked" :binary="true" inputId="checkbox1" />
    </div>
    <div class="flex flex-col gap-2 py-2">
      <Checkbox v-model="unchecked" :binary="true" inputId="checkbox1" />
      <label for="checkbox1" class="flex justify-center">Bottom Label</label>
    </div>
    <div class="flex gap-2 py-2">
      <label for="checkbox1">Left Label</label>
      <Checkbox v-model="unchecked" :binary="true" inputId="checkbox1" />
    </div>
    <div class="flex gap-2 py-2">
      <Checkbox v-model="unchecked" :binary="true" inputId="checkbox1" />
      <label for="checkbox1">Right Label</label>
    </div>
  </div>
  <h3 class="header-md pb-1">Radio Button</h3>
  <div class="flex flex-col items-start justify-items-start gap-4 pb-4">
    <div class="flex gap-2">
      <label for="radiobutton1">Radio Button</label>
      <RadioButton v-model="rb1" value="x" inputId="radiobutton1" name="rb1" />
    </div>
    <div class="flex gap-2">
      <label for="radiobutton2">Radio Button Selected</label>
      <RadioButton v-model="rb2" value="a" inputId="radiobutton2" />
    </div>
    <div class="flex gap-2">
      <label for="radiobutton3">Disabled Radio Button</label>
      <RadioButton v-model="rb3" value="x" disabled inputId="radiobutton3" />
    </div>
    <div class="flex gap-2">
      <label for="radiobutton4">Disabled Radio Button Selected</label>
      <RadioButton v-model="rb4" value="b" disabled inputId="radiobutton4" />
    </div>
    <div class="flex items-center gap-2">
      <label for="radiobutton5">Small Radio Button</label>
      <RadioButton v-model="rb5" value="x" inputId="radiobutton5" size="small" gbTheme="purple" />
    </div>
    <div class="flex items-center gap-2">
      <label for="radiobutton6">Small Radio Button Selected</label>
      <RadioButton v-model="rb6" value="c" inputId="radiobutton6" size="small" gbTheme="purple" />
    </div>
    <div class="flex items-center gap-2">
      <label for="radiobutton7">Small Disabled Radio Button</label>
      <RadioButton
        v-model="rb7"
        value="x"
        disabled
        inputId="radiobutton7"
        size="small"
        gbTheme="purple"
      />
    </div>
    <div class="flex items-center gap-2">
      <label for="radiobutton8">Small Disabled Radio Button Selected</label>
      <RadioButton
        v-model="rb8"
        value="d"
        disabled
        inputId="radiobutton8"
        size="small"
        gbTheme="purple"
      />
    </div>
  </div>
</template>
